import { useMemo } from "react";
import {
  Warning,
  Info,
  CaretDown,
  CaretUp,
  Prohibit,
} from "@phosphor-icons/react";
import DatePicker from "../../../ui/DatePicker";
import TimePicker from "../../../ui/TimePicker";
import {
  RECURRING_FREQUENCY,
  calculateBlockedTimeDates,
} from "../../../../store/services/calendarApi";
import { HEBREW_DAYS_FULL, formatHebrewDate } from "../../../../utils/dateUtils";
import {
  getClinicCalendarStartHour,
  formatCalendarHour,
} from "../../../../utils/calendarHours";

// Quick duration chips — the common block lengths.
const DURATION_CHIPS = [
  { minutes: 60, label: "שעה" },
  { minutes: 120, label: "שעתיים" },
  { minutes: 180, label: "3 שעות" },
];

// "Full day" = clinic calendar start hour (grid's first row) → 23:59.
const END_OF_DAY_MINUTES = 23 * 60 + 59;

// Repeat options (חזרה). Values are RECURRING_FREQUENCY constants so they map
// straight onto the block-series generator.
const REPEAT_OPTIONS = [
  { value: "none", label: "ללא חזרה" },
  { value: RECURRING_FREQUENCY.DAILY, label: "כל יום" },
  { value: RECURRING_FREQUENCY.WEEKLY, label: "כל שבוע" },
  { value: RECURRING_FREQUENCY.MONTHLY_DATE, label: "כל חודש בתאריך זה" },
  { value: RECURRING_FREQUENCY.YEARLY, label: "כל שנה בתאריך זה" },
];

/**
 * Blocked-time (חסימת זמן פרטי) single screen — cause, comments, date/time,
 * duration with quick chips, and an optional repeat with an until-date.
 * Replaces the schedule/details/summary steps entirely for blocks.
 */
function BlockTimeStep({ form }) {
  const {
    formData,
    updateField,
    stepErrors,
    overlappingAppointment,
    isEditing,
    appointment,
    clinicProfile,
  } = form;

  // Full-day chip: start at the calendar's first grid hour, end at 23:59.
  const calendarStartHour = getClinicCalendarStartHour(clinicProfile);
  const fullDayStartTime = formatCalendarHour(calendarStartHour);
  const fullDayMinutes = END_OF_DAY_MINUTES - calendarStartHour * 60;
  const isFullDayActive =
    formData.startTime === fullDayStartTime &&
    parseInt(formData.duration, 10) === fullDayMinutes;
  const selectFullDay = () => {
    updateField("startTime", fullDayStartTime);
    updateField("duration", fullDayMinutes);
  };

  const dayName = formData.date
    ? HEBREW_DAYS_FULL[new Date(`${formData.date}T00:00:00`).getDay()]
    : null;

  const adjustDuration = (direction) => {
    const current = parseInt(formData.duration, 10);
    const base = Number.isFinite(current) ? current : 60;
    updateField("duration", Math.max(1, base + direction * 5));
  };

  // Derived end time — display only (start + duration)
  const endTime = (() => {
    const dur = parseInt(formData.duration, 10);
    if (!formData.startTime || !Number.isFinite(dur) || dur <= 0) return null;
    const [h, m] = formData.startTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const total = h * 60 + m + dur;
    return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  })();

  const isRepeating = formData.blockRepeat !== "none";
  const isMonthlyClampable =
    formData.blockRepeat === RECURRING_FREQUENCY.MONTHLY_DATE &&
    formData.date &&
    new Date(`${formData.date}T00:00:00`).getDate() >= 29;

  // Occurrence preview — same generator the save path uses.
  const occurrenceCount = useMemo(() => {
    if (!isRepeating || !formData.date || !formData.startTime || !formData.blockUntilDate) {
      return null;
    }
    const start = new Date(`${formData.date}T${formData.startTime}`);
    const until = new Date(`${formData.blockUntilDate}T23:59:59`);
    if (isNaN(start.getTime()) || isNaN(until.getTime()) || until <= start) return null;
    return calculateBlockedTimeDates(start, formData.blockRepeat, until).length;
  }, [isRepeating, formData.date, formData.startTime, formData.blockRepeat, formData.blockUntilDate]);

  return (
    <div className="apt-wiz-step-panel" key="step-block-time">
      <div className="apt-wiz-hint-card">
        <Prohibit size={14} weight="fill" />
        <span>
          הזמן ייחסם ביומן באדום. הסיבה וההערות גלויות רק לך ולמנהל/ת הקליניקה.
        </span>
      </div>

      <div className="apt-wiz-form-row">
        <div className="apt-wiz-form-group">
          <label>סיבה (לא חובה)</label>
          <input
            type="text"
            value={formData.blockCause}
            onChange={(e) => updateField("blockCause", e.target.value)}
            placeholder="לדוגמה: הפסקה, פגישה אישית..."
            maxLength={80}
          />
        </div>
        <div className="apt-wiz-form-group">
          <label>הערות (לא חובה)</label>
          <textarea
            value={formData.blockComments}
            onChange={(e) => updateField("blockComments", e.target.value)}
            rows={1}
            maxLength={300}
            className="apt-wiz-block-comments"
          />
        </div>
      </div>

      <div className="apt-wiz-form-row">
        <div className="apt-wiz-form-group">
          <label>תאריך{dayName ? ` — יום ${dayName}` : ""}</label>
          <DatePicker
            name="date"
            value={formData.date}
            onChange={(e) => updateField("date", e.target.value)}
            error={stepErrors.date}
          />
          {stepErrors.date && (
            <div className="apt-wiz-field-error">{stepErrors.date}</div>
          )}
        </div>
        <div className="apt-wiz-form-group">
          <label>שעת התחלה</label>
          <TimePicker
            name="startTime"
            value={formData.startTime}
            onChange={(e) => updateField("startTime", e.target.value)}
            error={stepErrors.startTime}
          />
          {stepErrors.startTime && (
            <div className="apt-wiz-field-error">{stepErrors.startTime}</div>
          )}
        </div>
      </div>

      <div className="apt-wiz-form-group">
        <label>משך החסימה</label>
        <div className="apt-wiz-block-chips">
          {DURATION_CHIPS.map((chip) => (
            <button
              key={chip.minutes}
              type="button"
              className={`apt-wiz-block-chip ${
                parseInt(formData.duration, 10) === chip.minutes ? "active" : ""
              }`}
              onClick={() => updateField("duration", chip.minutes)}
            >
              {chip.label}
            </button>
          ))}
          <button
            type="button"
            className={`apt-wiz-block-chip ${isFullDayActive ? "active" : ""}`}
            onClick={selectFullDay}
            title={`${fullDayStartTime}–23:59`}
          >
            יום שלם
          </button>
        </div>
      </div>

      <div className="apt-wiz-form-row">
        <div className="apt-wiz-form-group">
          <label>משך (דקות)</label>
          <div className="apt-wiz-amount-box apt-wiz-duration-box">
            <button
              type="button"
              className="apt-wiz-amount-btn"
              onClick={() => adjustDuration(-1)}
              aria-label="הפחתת 5 דקות"
            >
              <CaretDown size={14} />
            </button>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={formData.duration}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  updateField("duration", "");
                  return;
                }
                const parsed = parseInt(raw, 10);
                if (Number.isFinite(parsed) && parsed >= 0) {
                  updateField("duration", parsed);
                }
              }}
              onKeyDown={(e) => {
                if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                e.preventDefault();
                adjustDuration(e.key === "ArrowUp" ? 1 : -1);
              }}
              className={stepErrors.duration ? "has-error" : ""}
              dir="ltr"
            />
            <button
              type="button"
              className="apt-wiz-amount-btn"
              onClick={() => adjustDuration(1)}
              aria-label="הוספת 5 דקות"
            >
              <CaretUp size={14} />
            </button>
          </div>
          {stepErrors.duration && (
            <div className="apt-wiz-field-error">{stepErrors.duration}</div>
          )}
        </div>
        <div className="apt-wiz-form-group">
          <label>שעת סיום</label>
          <div className="apt-wiz-endtime-display" dir="ltr">
            {endTime || "—"}
          </div>
        </div>
      </div>

      {/* Repeat — fixed after creation, like a meeting series' frequency */}
      {!isEditing && (
        <div className="apt-wiz-form-row">
          <div className="apt-wiz-form-group">
            <label>חזרה</label>
            <select
              value={formData.blockRepeat}
              onChange={(e) => updateField("blockRepeat", e.target.value)}
            >
              {REPEAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {isRepeating && (
            <div className="apt-wiz-form-group">
              <label>עד תאריך</label>
              <DatePicker
                name="blockUntilDate"
                value={formData.blockUntilDate}
                onChange={(e) => updateField("blockUntilDate", e.target.value)}
                error={stepErrors.blockUntilDate}
              />
              {stepErrors.blockUntilDate && (
                <div className="apt-wiz-field-error">{stepErrors.blockUntilDate}</div>
              )}
            </div>
          )}
        </div>
      )}

      {isEditing && appointment?.isRecurring && (
        <div className="apt-wiz-hint-card">
          <Info size={14} weight="fill" />
          <span>
            חסימה חוזרת ({appointment.recurringIndex}/{appointment.recurringTotalCount})
            — בשמירה ניתן לבחור: חסימה זו בלבד, זו והבאות, או כל הסדרה.
          </span>
        </div>
      )}

      {isMonthlyClampable && (
        <div className="apt-wiz-hint-card">
          <Info size={14} weight="fill" />
          <span>בחודשים קצרים החסימה תיווצר ביום האחרון של החודש.</span>
        </div>
      )}

      {occurrenceCount != null && (
        <div className="apt-wiz-hint-card">
          <Info size={14} weight="fill" />
          <span>
            ייווצרו {occurrenceCount} חסימות עד{" "}
            {formatHebrewDate(new Date(`${formData.blockUntilDate}T00:00:00`), "dd/MM/yyyy")}
          </span>
        </div>
      )}

      {/* Overlap is informational only — a block may cover existing meetings */}
      {overlappingAppointment && !isEditing && (
        <div className="apt-wiz-hint-card warning">
          <Warning size={14} weight="fill" />
          <span>
            קיימת פגישה בטווח הזמן הזה: {overlappingAppointment.patientName} בשעה{" "}
            {new Date(overlappingAppointment.startTime).toTimeString().slice(0, 5)}
            {" — "}החסימה תיווצר בכל מקרה.
          </span>
        </div>
      )}
    </div>
  );
}

export default BlockTimeStep;
